import React from 'react';
import { Link } from 'react-router-dom';
import ParticleCanvas from '../components/ParticleCanvas/ParticleCanvas';
import Typewriter from '../components/Typewriter/Typewriter';
import CountdownTimer from '../components/CountdownTimer/CountdownTimer';
import ClassShowcase from '../components/ClassShowcase/ClassShowcase';
import LoreQuote from '../components/LoreQuote/LoreQuote';
import NewsCard from '../components/NewsCard/NewsCard';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { DISCORD_URL } from '../config';
import { NEWS } from '../data/news';
import styles from './Home.module.css';

const RATES = [
  { label: 'XP Rate', value: 'x1' },
  { label: 'SP Rate', value: 'x1' },
  { label: 'Adena Rate', value: 'x1' },
  { label: 'Drop Rate', value: 'x1' },
];

const FEATURES = [
  {
    title: 'Zero Pay-to-Win',
    desc: 'No shortcuts for sale. Every level, every piece of gear, every castle siege victory is earned through skill and dedication alone.',
  },
  {
    title: 'Active Community',
    desc: 'Veteran players, organized clans, and a Discord that stays active. You will never feel like you are playing alone.',
  },
  {
    title: 'Long-Term Vision',
    desc: 'No shortcuts, no cash grabs. We are building for years - with transparency, regular updates and a commitment to fairness.',
  },
];

const HOW_TO = [
  {
    step: '01',
    title: 'Download the Client',
    desc: 'Grab the Lineage II Guardians client from our Download page. Windows only, around 15 GB. The client is pre-configured to connect to our server.',
  },
  {
    step: '02',
    title: 'Create Your Account',
    desc: 'Register a free account on this website. Your login and password work directly inside the game client - no separate launcher account needed.',
  },
  {
    step: '03',
    title: 'Enter the World',
    desc: 'Launch the game, log in, choose your race and class, and step into Aden. From your first hunt to your first siege - the adventure is yours.',
  },
];

function Section({ children, className }) {
  const [ref, visible] = useScrollAnimation();
  return (
    <div ref={ref} className={`reveal ${visible ? 'visible' : ''} ${className || ''}`}>
      {children}
    </div>
  );
}

export default function Home() {
  return (
    <main className={styles.main}>
      {/* Hero */}
      <section className={styles.hero} aria-label="Hero">
        <ParticleCanvas />
        <div className={styles.heroOverlay} aria-hidden="true" />
        <div className={styles.heroContent}>
          <p className={styles.heroEyebrow}>LINEAGE II GUARDIANS</p>
          <h1 className={styles.heroTitle}>Reawaken the Legend</h1>
          <p className={styles.heroSub}>
            <Typewriter />
          </p>
          <CountdownTimer />
          <div className={styles.heroCtas}>
            <Link to="/download" className={styles.ctaFilled}>Download Now</Link>
            <Link to="/account" className={styles.ctaOutline}>Create Account</Link>
          </div>
        </div>
      </section>

      {/* How to Get Started */}
      <section className={styles.howToSection} aria-label="How to get started">
        <Section>
          <h2 className="section-title">Get Started in 3 Steps</h2>
          <hr className="gold-divider" />
          <div className={styles.howToGrid}>
            {HOW_TO.map(({ step, title, desc }) => (
              <div key={step} className={styles.howToCard}>
                <span className={styles.stepNum} aria-hidden="true">{step}</span>
                <h3 className={styles.stepTitle}>{title}</h3>
                <p className={styles.stepDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Server Rates */}
      <section className={styles.ratesSection} aria-label="Server Rates">
        <Section>
          <h2 className="section-title">Server Rates</h2>
          <hr className="gold-divider" />
          <div className={styles.ratesGrid}>
            {RATES.map(({ label, value }) => (
              <div key={label} className={styles.rateCard}>
                <span className={styles.rateValue}>{value}</span>
                <span className={styles.rateLabel}>{label}</span>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Lore Quote */}
      <div className={styles.loreSection}>
        <LoreQuote />
      </div>

      {/* Character Races */}
      <div className={styles.classSection}>
        <ClassShowcase />
      </div>

      {/* Features */}
      <section className={styles.featuresSection} aria-label="Features">
        <Section>
          <h2 className="section-title">Why Play With Us</h2>
          <hr className="gold-divider" />
          <div className={styles.featuresGrid}>
            {FEATURES.map(({ title, desc }) => (
              <div key={title} className={styles.featureCard}>
                <div className={styles.featureAccent} aria-hidden="true" />
                <h3 className={styles.featureTitle}>{title}</h3>
                <p className={styles.featureDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Latest News */}
      <section className={styles.newsSection} aria-label="Latest news">
        <Section>
          <h2 className="section-title">Latest News</h2>
          <hr className="gold-divider" />
          <div className={styles.newsGrid}>
            {NEWS.map(item => <NewsCard key={item.id} {...item} />)}
          </div>
        </Section>
      </section>

      {/* Discord Banner */}
      <section className={styles.discordBanner} aria-label="Discord community">
        <Section>
          <div className={styles.discordInner}>
            <h2 className={styles.discordTitle}>Join our Discord Community</h2>
            <p className={styles.discordSub}>
              Connect with fellow warriors, get support, stay updated and be part of the founding community.
            </p>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.discordBtn}
            >
              Join Discord
            </a>
          </div>
        </Section>
      </section>
    </main>
  );
}


